// hooks
import { Fragment, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useHttp from "hooks/use-http";

// routes
import { useParams, useNavigate } from "react-router-dom";

// material styles
import { Grid, TextField, Button } from "@mui/material";

// material icons
import { ReportGmailerrorred, Check } from "@mui/icons-material/";

// request methods
import {
  getTeacherClassScoreByIdRequest,
  updateTeacherClassScoreByIdRequest,
} from "api/requests";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import BackButton from "components/shared/BackButton";
import DatePickerCM from "components/shared/DatePicker";

// helper
import Toast from "helper/toast";
import { convertNum } from "helper/convert-num";

// styled component
import {
  StyledStack,
  StyledTitle,
  StyledDateBox,
  StyledStudentTitle,
  StyledStudentName,
  ButtonContainer,
} from "./EditScore.style";
import CheckBoxBtn from "components/Score/CheckBox";
import { AddScoreSchema } from "validations";
import { DateObject } from "react-multi-date-picker";
import { persian_fa } from "react-date-object/locales/persian_fa";
import CheckBoxWithField from "components/Score/CheckBox";

export default function AddTeacherClassScore() {
  const { class_id, score_id } = useParams();

  const navigate = useNavigate();

  const { request, loading } = useHttp();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  // class name
  const [className] = useState("");

  const [studentsData, setStudentsData] = useState({
    data: [{ content: "دانش اموزی یافت نشد" }],
    count: 0,
  });

  // date and time state
  const [date, setDate] = useState("");
  const [newDate, setNewDate] = useState(null);

  // date and time handler
  const onDateTimeHandler = (value) =>
    setNewDate(
      value
        .format("YYYY-MM-DD")
        .split("-")
        .map((item) => convertNum(item))
        .join("-")
    );

  // add class score
  const onSubmit = async (data) => {
    const students = [];
    const scores = Object.entries(data.students.score);
    Object.entries(data.students.is_present).map((item, index) => {
      return !item[1] && scores[index][1] !== ""
        ? students.push({
            student_id: item[0].toString(),
            score: scores[index][1],
            is_present: !item[1],
          })
        : students.push({
            student_id: item[0].toString(),
            score: null,
            is_present: false,
          });
    });
    if (students.length < 1) {
      return Toast({
        type: "custom-err",
        title: "برای هیچ دانش آموزی نمره ثبت نشده",
      });
    }
    const res = await request({
      url: () =>
        updateTeacherClassScoreByIdRequest({
          ...data,
          id: score_id,
          date: newDate ?? null,
          students,
        }),
    });
    if (res) {
      Toast({ type: "success", title: "ویرایش نمرات " });
      reset({
        date: "",
        class_course_id: "",
        students: "",
      });
      navigate(-1);
    }
  };

  // // get class score data
  const getClassStudnetsData = useCallback(async () => {
    const res = await request({
      url: getTeacherClassScoreByIdRequest,
      values: score_id,
    });

    if (!res?.data) {
      setStudentsData({
        data: [{ content: "دانش اموزی یافت نشد" }],
        count: 0,
      });
    } else {
      const transformedData = res.data.studentScores.map((item) => ({
        id: item.student.id,
        first_name: item?.student?.first_name,
        last_name: item?.student?.last_name,
        score: item.score,
        is_present: item.is_present,
      }));

      setStudentsData({
        data: transformedData,
        count: res.count,
      });

      setDate(
        new DateObject({
          date: res.data.date.timestamp * 1000,
          calendar: persian_fa,
        })
      );
    }
  }, []);

  useEffect(() => getClassStudnetsData(), [getClassStudnetsData]);

  return (
    <DrawerCM title="ویرایش نمرات کلاسی" $styledHeader>
      {loading ? (
        <LoadingCM />
      ) : (
        <Fragment>
          <Grid container>
            <Grid item mt={3} xs={12}>
              <BackButton Route={`/teacher_class/${class_id}/scores`} />
            </Grid>
            <StyledStack>
              <Grid>
                <StyledTitle>{`ویرایش نمرات کلاس ${className}`}</StyledTitle>
                <Grid container>
                  <Fragment>
                    {studentsData.count < 1 ? (
                      <Grid item xs={12} p={1.5}>
                        <Button
                          color="error"
                          startIcon={<ReportGmailerrorred />}
                        >
                          برای این کلاس دانش آموزی ثبت نشده
                        </Button>
                      </Grid>
                    ) : (
                      <Grid container>
                        <Grid item sm={5} xs={12} p={1.5}>
                          <StyledDateBox>
                            <DatePickerCM
                              onChange={onDateTimeHandler}
                              value={date ?? ""}
                              render={(value, openCalendar) => {
                                return (
                                  <TextField
                                    label="تاریخ"
                                    variant="outlined"
                                    id="date"
                                    {...register("date")}
                                    error={Boolean(errors.date)}
                                    helperText={errors.date?.message}
                                    value={value ? value : ""}
                                    onClick={openCalendar}
                                  />
                                );
                              }}
                            />
                          </StyledDateBox>
                        </Grid>
                        <Grid item xs={12} p={1.5}>
                          <StyledStudentTitle>
                            نام و نام خانوادگی دانش آموز:
                          </StyledStudentTitle>
                        </Grid>
                        {studentsData.data?.map((item, index) => (
                          <Fragment key={item.id}>
                            <Grid item sm={6} xs={12} p={1.5}>
                              <StyledStudentName>{`${item.first_name} ${item.last_name}`}</StyledStudentName>
                            </Grid>
                            <CheckBoxWithField
                              id={`students.is_present[${item.id}]`}
                              isPresent={!item.is_present}
                              registerField={{
                                ...register(`students.score[${item.id}]`),
                              }}
                              fieldProps={{
                                defaultValue: !item.is_present
                                  ? ""
                                  : item.score,
                              }}
                              register={{
                                ...register(`students.is_present[${item.id}]`),
                              }}
                            ></CheckBoxWithField>
                          </Fragment>
                        ))}
                      </Grid>
                    )}
                  </Fragment>
                </Grid>
              </Grid>

              <ButtonContainer>
                <Button
                  color="success"
                  startIcon={<Check />}
                  variant="contained"
                  sx={{ mt: 1, mr: 1 }}
                  onClick={handleSubmit(onSubmit)}
                >
                  ثبت
                </Button>
              </ButtonContainer>
            </StyledStack>
          </Grid>
        </Fragment>
      )}
    </DrawerCM>
  );
}
